/**
 * Content Analysis API Service
 * 
 * Fetches content quality metrics from core-api.
 */

import { api } from '@/services/api';
import type { 
  ContentAnalysisSummaryResponse, 
  ContentAnalysisPageDetailResponse,
  ContentAnalysisTrendsResponse 
} from '@/types/customer';

// Helper function to convert snake_case to camelCase
function toCamelCase(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map(toCamelCase);
  } else if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj).reduce((acc, key) => {
      const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
      acc[camelKey] = toCamelCase(obj[key]);
      return acc;
    }, {} as any);
  }
  return obj;
}

export interface GetPagesParams {
  page?: number;
  size?: number;
  sortBy?: 'word_count' | 'sentiment_score' | 'content_quality_score' | 'analyzed_at';
  sortDirection?: 'ASC' | 'DESC';
  qualityFilter?: 'EXCELLENT' | 'GOOD' | 'NEEDS_IMPROVEMENT' | 'CRITICAL';
}

export interface GetTrendsParams {
  startDate?: string; // ISO date
  endDate?: string;   // ISO date
}

/**
 * Get content analysis summary for a site
 */
export async function getContentAnalysisSummary(siteId: number): Promise<ContentAnalysisSummaryResponse> {
  const response = await api.get<{ success: boolean; data: any }>(
    `/webtools/sites/${siteId}/content-analysis/summary`
  );
  return toCamelCase(response.data.data);
}

/**
 * Get paginated content analysis page details
 */
export async function getContentAnalysisPages(
  siteId: number, 
  params: GetPagesParams = {}
): Promise<ContentAnalysisPageDetailResponse> {
  const response = await api.get<{ success: boolean; data: any }>(
    `/webtools/sites/${siteId}/content-analysis/pages`,
    { params }
  );
  return toCamelCase(response.data.data);
}

/**
 * Get content quality trends over time
 */
export async function getContentAnalysisTrends(
  siteId: number,
  params: GetTrendsParams = {}
): Promise<ContentAnalysisTrendsResponse> {
  const response = await api.get<{ success: boolean; data: any }>(
    `/webtools/sites/${siteId}/content-analysis/trends`,
    { params }
  );
  return toCamelCase(response.data.data);
}

export default {
  getContentAnalysisSummary,
  getContentAnalysisPages,
  getContentAnalysisTrends,
};
